import { notifications } from "@mantine/notifications"
import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { ProductType } from "@tfg/db"
import { AxiosError } from "axios"

import { AdminRequests, AdminResponses } from "@tfg/types/endpoints/admin"
import { EcobalyseProductType } from "@tfg/types/endpoints/analysis"
import { ProductTypeUpdateValidationType } from "@tfg/types/schemas/admin"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"

export const useListProductTypesQuery = () =>
  useQuery(["productTypes"], async () => {
    const res = await httpClient.get<AdminResponses.ProductTypes[]>(
      "/admin/product-types"
    )
    return res.data
  })

export const useCreateProductTypeMutation = (
  options?: UseMutationOptions<
    ProductType,
    AxiosError,
    AdminRequests.NewProductType
  >
) =>
  useMutation<ProductType, AxiosError, ProductTypeUpdateValidationType>({
    ...options,
    mutationFn: async (productType: ProductTypeUpdateValidationType) => {
      const res = await httpClient.post<ProductType>(
        "/admin/product-types",
        productType
      )
      return res.data
    },
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      await queryClient.invalidateQueries(["productTypes"])
    },
    onError: (error, variables) => {
      notifications.show({
        title: `Error ${variables.name}`,
        message: error.message || "An error occurred",
        color: "red",
      })
    },
  })

export const useUpdateProductTypeMutation = (
  options?: UseMutationOptions<
    ProductType,
    AxiosError,
    { productId: number; productType: AdminRequests.NewProductType }
  >
) =>
  useMutation<
    ProductType,
    AxiosError,
    { productId: number; productType: AdminRequests.NewProductType }
  >({
    mutationFn: async ({ productId, productType }) => {
      const res = await httpClient.patch<ProductType>(
        `/admin/product-types/${productId}`,
        productType
      )
      return res.data
    },
    onSuccess: async (_data, variables, context) => {
      options?.onSuccess?.(_data, variables, context)
      await queryClient.invalidateQueries(["productTypes"])
      notifications.show({
        title: "Updated",
        message: `Product type ${variables.productType.name} has been updated`,
        color: "green",
      })
    },
    onError: (error, variables) => {
      notifications.show({
        title: `Error ${variables.productType.name}`,
        message: error.message || "An error occurred",
        color: "red",
      })
    },
  })

export const useDeleteProductTypeMutation = (
  options?: UseMutationOptions<void, unknown, number>
) => {
  return useMutation<void, unknown, number>({
    ...options,
    mutationFn: async (productId) => {
      return httpClient.delete(`/admin/product-types/${productId}`)
    },
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
      notifications.show({
        title: "Deleted",
        message: `Product type ${variables} has been deleted`,
        color: "green",
      })
      await queryClient.invalidateQueries(["productTypes"])
    },
  })
}

export const useListEcobalyseProductTypesQuery = () =>
  useQuery(["ecobalyse", "productTypes"], async () => {
    const res = await httpClient.get<EcobalyseProductType[]>(
      "/analysis/productTypes"
    )
    return res.data
  })
